/**
 * Owns MPV's report-export portal flow: the popup `Window` ref, the
 * pending pre-fill state passed in from notes, the active-export state
 * the portal renders into, and the open / close-on-unmount lifecycle.
 * Extracted from MPV's component body so the report-export concern lives
 * in one place.
 */

import queryString from 'query-string';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {ReportExportState} from 'app/components/NotesReports/ReportsView';
import {usePushNotification} from 'app/components/Notification';
import {SidebarViewState} from 'app/components/TabbedSidebar/TabbedSidebar';
import * as routeUtils from 'app/utils/routeUtils';

interface ActiveReportExport {
  htmlEl: HTMLElement;
  allParts: boolean;
  savedReportId: number | undefined;
  reportTitle: string | undefined;
  refetchReports: (() => Promise<void>) | undefined;
}

interface Args {
  setTabbedSidebarState: (
    state: SidebarViewState | ((prev: SidebarViewState) => SidebarViewState)
  ) => void;
}

export interface ReportExportPortal {
  pendingNewReportExportState: ReportExportState | null;
  activeReportExport: ActiveReportExport | null;
  reportExportWindowRef: React.MutableRefObject<Window | null>;
  setReportExportModalState: (state: ReportExportState | null) => void;
  openReportExportModal: (state: ReportExportState) => void;
}

export default function useReportExportPortal({setTabbedSidebarState}: Args): ReportExportPortal {
  const [pendingNewReportExportState, setPendingNewReportExportState] =
    React.useState<ReportExportState | null>(null);
  const [activeReportExport, setActiveReportExport] = React.useState<ActiveReportExport | null>(
    null
  );
  const reportExportWindowRef = React.useRef<Window | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const pushNotification = usePushNotification();

  /**
   * Sets ReportExportModal and opens the reports pane.
   */
  const setReportExportModalState = React.useCallback(
    (reportExportState: ReportExportState | null) => {
      setPendingNewReportExportState(reportExportState);
      setTabbedSidebarState({
        isExpanded: true,
        currentViewTitle: 'Reports',
      });
    },
    [setTabbedSidebarState]
  );

  // A link from the portfolio's Documents tab carries ?report=<id>. It opens the
  // Reports tab, which opens that saved report once the list has loaded (see
  // ReportsView), and is consumed at once so it can't reopen the report later.
  const reportIdFromUrl = React.useMemo(
    () =>
      routeUtils.getStringParamFromQueryParams(
        queryString.parse(location.search),
        routeUtils.OPEN_REPORT_QUERY_PARAM
      ),
    [location.search]
  );
  React.useEffect(() => {
    if (reportIdFromUrl === undefined) {
      return;
    }
    const savedReportId = parseInt(reportIdFromUrl, 10);
    if (!Number.isNaN(savedReportId)) {
      setReportExportModalState({newTab: false, allParts: false, savedReportId});
    }
    // ?chat=open (useTabbedSidebarState) would also claim the sidebar in this
    // same pass and, stripping from the same stale search, put ?report back.
    // A specific report wins over the generic chat tab, so consume both here.
    const nextSearch = routeUtils.removeQueryParamFromSearch(
      routeUtils.removeQueryParamFromSearch(location.search, routeUtils.OPEN_REPORT_QUERY_PARAM),
      routeUtils.OPEN_CHAT_QUERY_PARAM
    );
    navigate(`${location.pathname}${nextSearch}${location.hash}`, {replace: true});
  }, [reportIdFromUrl, location, navigate, setReportExportModalState]);

  /**
   * @param newTab While in general we want to open the report export in a new
   * window so we can control the size (to make it more print-preview) and
   * disable navigation, we allow for Control- or Command-clicking the button to
   * open into a new tab to facilitate screen sharing demos. (Opening in a new
   * tab means that the presenter doesn't have to change the window that they're
   * screen sharing.)
   */
  function openReportExportModal({
    newTab,
    allParts,
    savedReportId,
    reportTitle,
    refetchReports,
  }: ReportExportState) {
    if (reportExportWindowRef.current) {
      setActiveReportExport((prev) => (prev ? {...prev, allParts} : prev));
      return;
    }

    reportExportWindowRef.current = window.open(
      '',
      '_blank',
      newTab ? undefined : 'toolbar=no,status=no,location=no,width=890,height=780'
    );

    if (!reportExportWindowRef.current) {
      // Browsers only allow window.open shortly after a click, so a report opened
      // from a deep link on page load can be blocked.
      pushNotification({
        message:
          'Your browser blocked the report window. Allow pop-ups for Lens, then open the report again.',
      });
      return;
    }

    reportExportWindowRef.current.addEventListener('unload', () => {
      reportExportWindowRef.current = null;
      setActiveReportExport(null);
    });

    const reportHtmlElement = reportExportWindowRef.current.document.querySelector('html')!;

    while (reportHtmlElement.firstChild) {
      reportHtmlElement.removeChild(reportHtmlElement.firstChild);
    }

    reportHtmlElement.setAttribute('mozNoMarginBoxes', 'mozNoMarginBoxes');
    reportHtmlElement.setAttribute('mozDisallowSelectionPrint', 'mozDisallowSelectionPrint');

    setActiveReportExport({
      htmlEl: reportHtmlElement,
      allParts,
      savedReportId,
      reportTitle,
      refetchReports,
    });
  }

  // Close the popup window if MPV unmounts while it's open.
  React.useEffect(() => {
    return () => {
      if (reportExportWindowRef.current) {
        reportExportWindowRef.current.close();
        reportExportWindowRef.current = null;
      }
    };
  }, []);

  return {
    pendingNewReportExportState,
    activeReportExport,
    reportExportWindowRef,
    setReportExportModalState,
    openReportExportModal,
  };
}
